import { GridView } from '@/components/dashboard/documents-page/DocumentsPageContent'
import {
  useDocumentTableColumns,
  DocumentsTable,
} from '@/components/dashboard/documents-page/DocumentsTable'
import { ReferenceDocumentsTable } from '@/components/dashboard/documents-page/ReferenceDocumentsTable'
import DocumentsSidebar from '@/components/dashboard/documents-page/DocumentsSidebar'
import { LoadingTable } from '@/components/loading-ui/LoadingTable'
import { LoadingDocumentsCard } from '@/components/loading-ui/LoadingDocumentsCard'
import { Separator } from '@/components/ui/separator'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Button } from '@/components/ui/button'
import { DraftPublishTabs } from '@/components/dashboard/DraftPublishTabs'
import { Input } from '@/components/ui/input'
import { MultiSelect } from '@/components/ui/multi-select'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogTitle,
  DialogHeader,
  DialogContent,
  DialogFooter,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { useLocalStorageState } from '@/hooks/useLocalStorageState'
import { useUrlSearchParamsState } from '@/hooks/useUrlSearchParamsState'
import { useSuspenseQuery } from '@tanstack/react-query'
import {
  projectDetailsOptions,
  allBookmarksInProjectOptions,
  projectDocumentsOptions,
  orgPermissionsCurrentUserOptions,
  tenantProjectsOptions,
  usersOptions,
} from '@/data/queryOptions'

import {
  createFileRoute,
  useNavigate,
  useParams,
  useRouteContext,
  Link,
} from '@tanstack/react-router'
import {
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  useReactTable,
  type SortingState,
  type OnChangeFn,
  type ColumnFilter,
} from '@tanstack/react-table'
import type { Project, DocumentData, UserInfo } from '@/data/entities'
import {
  canAccessWorkspace,
  getProjectRoles,
  getVisibleProjects,
  getWorkspaceAccess,
} from '@/data/entities'
import {
  ArrowDownUpIcon,
  MoreHorizontal,
  FolderIcon,
  LayoutGridIcon,
  ListFilterIcon,
  ListIcon,
  UsersIcon,
} from 'lucide-react'
import {
  Suspense,
  useState,
  useCallback,
  useMemo,
  useTransition,
  useEffect,
} from 'react'
import { useIdTokenAndTenantId } from '@/hooks/useIdTokenAndTenantId'
import { REFRESH_INTERVAL } from '@/data/constants'
import { ShareButton } from '@/components/document-edit/Share'
import { useIsMobile } from '@/hooks/use-mobile'
import { cn } from '@/lib/utils'
import { ExportCSVDialog } from '@/components/dashboard/documents-page/ExportCSVDialog'

import { DashboardLayout } from '@/routes/_authenticated/_dashboard'
import { DraftModeBanner } from '@/components/document-edit/DraftModeBanner'
import { LoadingAllDocumentsContent } from '@/components/loading-ui/LoadingAllDocumentsContent'
import { z } from 'zod'
import NewFolderDialog from '@/components/dashboard/documents-page/NewFolderDialog'
import { WorkspaceUserAccessDialog } from '@/components/dashboard/documents-page/WorkspaceUserAccessDialog'

const SORT_OPTIONS = [
  { value: 'title-asc', label: 'Name (A -> Z)', id: 'title', desc: false },
  { value: 'title-desc', label: 'Name (Z -> A)', id: 'title', desc: true },
  {
    value: 'updatedAt-desc',
    label: 'Last Modified - Newest First',
    id: 'updatedAt',
    desc: true,
  },
  {
    value: 'updatedAt-asc',
    label: 'Last Modified - Oldest First',
    id: 'updatedAt',
    desc: false,
  },
] as const

function getSortValue(sorting: SortingState): string {
  if (sorting.length === 0) return 'updatedAt-desc'
  const { id, desc } = sorting[0]
  return `${id}-${desc ? 'desc' : 'asc'}`
}

type ProjectTabValue = 'documents' | 'reference-documents'

type ProjectRouteSearch = {
  stage?: 'all' | 'published' | 'draft'
  folder?: string
  tab?: ProjectTabValue
}

const ProjectRouteSearchSchema = z.object({
  stage: z.enum(['all', 'published', 'draft']).optional(),
  folder: z.string().optional(),
  tab: z.enum(['documents', 'reference-documents']).optional(),
})
export const Route = createFileRoute(
  '/_authenticated/org/$orgId/projects/$projectId/',
)({
  component: RouteComponent,
  validateSearch: (search: ProjectRouteSearch) => {
    return ProjectRouteSearchSchema.parse(search)
  },
})

function RouteComponent() {
  const { isOrgAdmin, isOrgEditor } = useRouteContext({
    from: '/_authenticated',
  })
  const { projectId } = useParams({
    from: '/_authenticated/org/$orgId/projects/$projectId/',
  })
  const navigate = useNavigate()
  const search = Route.useSearch()

  const { idToken, TENANT_ID } = useIdTokenAndTenantId()
  const isMobile = useIsMobile()

  const canEditOrg = isOrgAdmin || isOrgEditor

  const { data: projects } = useSuspenseQuery(
    tenantProjectsOptions(TENANT_ID!, idToken!),
  )
  const { data: orgPermissions } = useSuspenseQuery(
    orgPermissionsCurrentUserOptions(TENANT_ID!, idToken!),
  )
  const workspaceAccess = useMemo(
    () => getWorkspaceAccess(orgPermissions, TENANT_ID!),
    [orgPermissions, TENANT_ID],
  )
  const visibleProjects = useMemo(
    () =>
      getVisibleProjects(projects ?? [], {
        isOrgAdmin: !!isOrgAdmin,
        isOrgEditor: !!isOrgEditor,
        workspaceAccess,
      }),
    [projects, workspaceAccess, isOrgAdmin, isOrgEditor],
  )
  const currentUserProjectRoles = useMemo(
    () => getProjectRoles(orgPermissions, TENANT_ID!),
    [orgPermissions, TENANT_ID],
  )
  const projectRole = currentUserProjectRoles.get(projectId)?.toUpperCase()
  const isProjectAdmin = projectRole === 'ADMIN'
  const isProjectEditor = projectRole === 'ADMIN' || projectRole === 'EDITOR'
  const canEdit = canEditOrg || isProjectEditor
  const canManageAccess = isOrgAdmin || isProjectAdmin

  const [stage, setStage] = useUrlSearchParamsState<
    'all' | 'published' | 'draft'
  >({
    name: 'stage',
    defaultValue: canEdit ? 'all' : 'published',
  })

  const [tab, setTab] = useUrlSearchParamsState<ProjectTabValue>({
    name: 'tab',
    defaultValue: 'documents',
  })

  const folder = search.folder

  const { data: allUsers } = useSuspenseQuery(
    usersOptions(TENANT_ID!, idToken!),
  )

  const [viewType, setViewType] = useLocalStorageState<
    'grid' | 'list' | 'table'
  >('documents-view-type', 'grid')
  const [sorting, setSorting] = useState<SortingState>([
    {
      id: 'updatedAt',
      desc: true,
    },
  ])
  const [globalFilterInputText, setGlobalFilterInputText] = useState('')
  const [typeFilter, setTypeFilter] = useState<string[]>([])
  const [statusFilter, setStatusFilter] = useState<string[]>([])
  const [creatorFilter, setCreatorFilter] = useState<string[]>([])
  const [bookmarkFilter, setBookmarkFilter] = useState<
    'all' | 'bookmarked' | 'not-bookmarked'
  >('all')
  const [_isPending, startTransition] = useTransition()

  const handleFilterTextChange = useCallback((value: string) => {
    startTransition(() => {
      setGlobalFilterInputText(value)
    })
  }, [])

  // Clear search when navigating to a new project or folder
  useEffect(() => {
    setGlobalFilterInputText('')
  }, [projectId, folder])

  if (!canEdit && stage !== 'published') {
    navigate({
      to: '/org/$orgId/projects/$projectId',
      params: { orgId: TENANT_ID!, projectId: projectId },
      search: { stage: 'published' },
      replace: true,
    })
    return null
  }

  const hasProjectAccess = canAccessWorkspace(projectId, {
    isOrgAdmin: !!isOrgAdmin,
    isOrgEditor: !!isOrgEditor,
    workspaceAccess,
  })

  if (!hasProjectAccess) {
    navigate({
      to: '/org/$orgId/documents',
      params: { orgId: TENANT_ID! },
      replace: true,
    })
    return null
  }

  return (
    <DashboardLayout>
      <main
        className={cn('main-container', isMobile && 'mt-15 mb-20 flex-col')}
      >
        <DocumentsSidebar projects={visibleProjects} />
        <Suspense fallback={<LoadingDocumentsCard />}>
          <ProjectPageContent
            projectId={projectId}
            stage={stage}
            setStage={setStage}
            tab={tab}
            setTab={setTab}
            viewType={viewType}
            setViewType={setViewType}
            sorting={sorting}
            setSorting={setSorting}
            globalFilterInputText={globalFilterInputText}
            typeFilter={typeFilter}
            setTypeFilter={setTypeFilter}
            statusFilter={statusFilter}
            setStatusFilter={setStatusFilter}
            creatorFilter={creatorFilter}
            setCreatorFilter={setCreatorFilter}
            bookmarkFilter={bookmarkFilter}
            setBookmarkFilter={setBookmarkFilter}
            handleFilterTextChange={handleFilterTextChange}
            allUsers={allUsers}
            projects={visibleProjects}
            canEdit={canEdit}
            canManageAccess={canManageAccess}
            folder={folder}
            isMobile={isMobile}
          />
        </Suspense>
      </main>
    </DashboardLayout>
  )
}

type ProjectPageContentProps = {
  projectId: string
  stage: 'all' | 'published' | 'draft'
  setStage: (stage: 'all' | 'published' | 'draft') => void
  tab: ProjectTabValue
  setTab: (tab: ProjectTabValue) => void
  viewType: 'grid' | 'list' | 'table'
  setViewType: (viewType: 'grid' | 'list' | 'table') => void
  sorting: SortingState
  setSorting: OnChangeFn<SortingState>
  globalFilterInputText: string
  typeFilter: string[]
  setTypeFilter: (value: string[]) => void
  statusFilter: string[]
  setStatusFilter: (value: string[]) => void
  creatorFilter: string[]
  setCreatorFilter: (value: string[]) => void
  bookmarkFilter: 'all' | 'bookmarked' | 'not-bookmarked'
  setBookmarkFilter: (value: 'all' | 'bookmarked' | 'not-bookmarked') => void
  handleFilterTextChange: (value: string) => void
  allUsers: UserInfo[]
  projects: Project[] | null | undefined
  canEdit: boolean
  canManageAccess: boolean
  folder?: string
  isMobile: boolean
}

function ProjectPageContent({
  projectId,
  stage,
  setStage,
  tab,
  setTab,
  viewType,
  setViewType,
  sorting,
  setSorting,
  globalFilterInputText,
  typeFilter,
  setTypeFilter,
  statusFilter,
  setStatusFilter,
  creatorFilter,
  setCreatorFilter,
  bookmarkFilter,
  setBookmarkFilter,
  handleFilterTextChange,
  allUsers,
  projects,
  canEdit,
  canManageAccess,
  folder,
  isMobile,
}: ProjectPageContentProps) {
  const { idToken, TENANT_ID } = useIdTokenAndTenantId()

  const { data: projectDetails } = useSuspenseQuery(
    projectDetailsOptions(idToken!, TENANT_ID!, projectId),
  )

  const [workspaceAccessOpen, setWorkspaceAccessOpen] = useState(false)
  const currentProject = projects?.find((p) => p.id === projectId)

  // Shared props for document wrapper components
  const wrapperProps: DocumentsWrapperProps = {
    projectId,
    viewType,
    sorting,
    setSorting,
    globalFilterInputText,
    typeFilter,
    setTypeFilter,
    statusFilter,
    setStatusFilter,
    creatorFilter,
    setCreatorFilter,
    bookmarkFilter,
    setBookmarkFilter,
    handleFilterTextChange,
    allUsers,
    projects,
    folder,
    isMobile,
  }

  return (
    <div className="dashboard-page-container">
      <div className="flex flex-col items-start gap-2 md:flex-row md:items-center">
        <div className="flex-1">
          {folder ? (
            <h1 className="flex items-center gap-2 text-2xl">
              <Link
                to="/org/$orgId/projects/$projectId"
                params={{ orgId: TENANT_ID!, projectId }}
                search={{}}
                className="hover:underline"
              >
                {projectDetails.name}
              </Link>
              <span className="text-copy-secondary flex items-center gap-2 text-sm font-normal select-none">
                /
                <FolderIcon className="size-4" />
                {folder}
              </span>
            </h1>
          ) : (
            <h1 className="text-2xl">{projectDetails.name}</h1>
          )}
        </div>
        <div className="flex w-full flex-row justify-between gap-2 md:w-fit md:justify-end">
          <div className="flex items-center gap-2">
            <ShareButton
              header="Workspace QR Code"
              text="Workspace QR Code"
              title={projectDetails.name}
              url={`${window.location.origin}/org/${TENANT_ID}/projects/${projectId}`}
            />
          </div>
          <Tabs
            value={viewType}
            onValueChange={(value) =>
              setViewType(value as 'grid' | 'list' | 'table')
            }
            tabIndex={-1}
          >
            <TabsList tabIndex={-1}>
              <TabsTrigger value="grid" tabIndex={-1}>
                <LayoutGridIcon />
              </TabsTrigger>
              <TabsTrigger value="list" tabIndex={-1}>
                <ListIcon />
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      </div>
      {canManageAccess && (
        <WorkspaceUserAccessDialog
          open={workspaceAccessOpen}
          onOpenChange={setWorkspaceAccessOpen}
          projectId={projectId}
          projectName={currentProject?.name ?? ''}
        />
      )}
      <Tabs
        value={tab}
        onValueChange={(value) => setTab(value as ProjectTabValue)}
        className="flex min-h-0 w-full flex-1 flex-col"
      >
        <TabsList variant="underline" className="w-full justify-start">
          <TabsTrigger value="documents" variant="underline">
            Documents
          </TabsTrigger>
          <TabsTrigger value="reference-documents" variant="underline">
            Reference Documents
          </TabsTrigger>
        </TabsList>
        <TabsContent
          value="documents"
          className="flex min-h-0 min-w-0 flex-col gap-4 outline-none"
        >
          {canEdit && (
            <div className="flex w-full items-center justify-between gap-4">
              <DraftPublishTabs stage={stage} onStageChange={setStage} />
              <div className="flex items-center gap-2">
                <ExportCSVDialog
                  projectId={projectId}
                  folder={folder}
                  stage={stage}
                  typeFilter={typeFilter}
                  creatorFilter={creatorFilter}
                  allUsers={allUsers}
                  projects={projects ?? []}
                />
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="outline"
                      size="icon"
                      aria-label="More options"
                    >
                      <MoreHorizontal />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <NewFolderDialog projectId={projectId}>
                      <DropdownMenuItem
                        onSelect={(event) => event.preventDefault()}
                      >
                        <FolderIcon className="size-4" />
                        <span>New Folder</span>
                      </DropdownMenuItem>
                    </NewFolderDialog>
                    {canManageAccess && (
                      <DropdownMenuItem
                        onSelect={() => setWorkspaceAccessOpen(true)}
                      >
                        <UsersIcon className="size-4" />
                        <span>Manage Workspace Access</span>
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          )}
          <Suspense fallback={<LoadingAllDocumentsContent />}>
            {!canEdit ? (
              <ProjectPublishedDocumentsWrapper
                {...wrapperProps}
                showDraftBanner={false}
              />
            ) : stage === 'published' ? (
              <ProjectPublishedDocumentsWrapper
                {...wrapperProps}
                showDraftBanner={false}
              />
            ) : stage === 'draft' ? (
              <ProjectDraftDocumentsWrapper
                {...wrapperProps}
                showDraftBanner={true}
              />
            ) : (
              <ProjectAllDocumentsWrapper
                {...wrapperProps}
                showDraftBanner={false}
              />
            )}
          </Suspense>
        </TabsContent>
        <TabsContent
          value="reference-documents"
          className="flex min-h-0 min-w-0 flex-col gap-4 outline-none"
        >
          <Suspense fallback={<LoadingTable />}>
            <ReferenceDocumentsTable projectId={projectId} />
          </Suspense>
        </TabsContent>
      </Tabs>
    </div>
  )
}

// Display component - receives documents as props
type ProjectDocumentsDisplayProps = {
  documents: DocumentData[]
  bookmarkedIds: Set<string>
  viewType: 'grid' | 'list' | 'table'
  sorting: SortingState
  setSorting: OnChangeFn<SortingState>
  globalFilterInputText: string
  typeFilter: string[]
  setTypeFilter: (value: string[]) => void
  statusFilter: string[]
  setStatusFilter: (value: string[]) => void
  creatorFilter: string[]
  setCreatorFilter: (value: string[]) => void
  bookmarkFilter: 'all' | 'bookmarked' | 'not-bookmarked'
  setBookmarkFilter: (value: 'all' | 'bookmarked' | 'not-bookmarked') => void
  handleFilterTextChange: (value: string) => void
  allUsers: UserInfo[]
  projects: Project[] | null | undefined
  projectId: string
  isMobile: boolean
  showDraftBanner?: boolean
}

function ProjectDocumentsDisplay({
  documents,
  bookmarkedIds,
  viewType,
  sorting,
  setSorting,
  globalFilterInputText,
  typeFilter,
  setTypeFilter,
  statusFilter,
  setStatusFilter,
  creatorFilter,
  setCreatorFilter,
  bookmarkFilter,
  setBookmarkFilter,
  handleFilterTextChange,
  allUsers,
  projects,
  projectId,
  isMobile,
  showDraftBanner = false,
}: ProjectDocumentsDisplayProps) {
  // Filter documents based on bookmark filter
  const filteredByBookmark = useMemo(() => {
    if (bookmarkFilter === 'all') return documents
    if (bookmarkFilter === 'bookmarked') {
      return documents.filter((doc) => bookmarkedIds.has(doc.id))
    }
    // not-bookmarked
    return documents.filter((doc) => !bookmarkedIds.has(doc.id))
  }, [documents, bookmarkFilter, bookmarkedIds])

  const typeFiltersList = useMemo(() => {
    return [
      ...new Set(filteredByBookmark.map((doc) => doc.metadata?.type)),
    ].filter((type) => type !== undefined) as string[]
  }, [filteredByBookmark])

  const statusFiltersList = useMemo(() => {
    return [...new Set(filteredByBookmark.map((doc) => doc.status))].filter(
      (status) => status !== undefined,
    )
  }, [filteredByBookmark])

  const columns = useDocumentTableColumns(filteredByBookmark)

  const columnFilters = useMemo(() => {
    return [
      typeFilter.length > 0 ? { id: 'type', value: typeFilter } : undefined,
      statusFilter.length > 0
        ? { id: 'status', value: statusFilter }
        : undefined,
      creatorFilter.length > 0
        ? { id: 'creator', value: creatorFilter }
        : undefined,
    ].filter(Boolean) as ColumnFilter[]
  }, [typeFilter, statusFilter, creatorFilter])

  const table = useReactTable({
    data: filteredByBookmark,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onSortingChange: setSorting,
    state: {
      sorting,
      globalFilter: globalFilterInputText,
      pagination: {
        pageIndex: 0,
        pageSize: 9999, // TODO: set up pagination from server
      },
      columnFilters,
    },
    columnResizeMode: 'onChange',
    defaultColumn: {
      size: 80,
      minSize: 80,
    },
  })

  // Stable reference for grid view — avoids re-rendering WasmDocumentCardGrid
  // when only filter/sort state changed but the visible rows are the same.
  const rows = table.getRowModel().rows
  const gridDocuments = useMemo(() => rows.map((row) => row.original), [rows])

  return (
    <>
      <div className="flex flex-row flex-nowrap items-center gap-2">
        <Input
          placeholder="Search documents"
          className="min-w-0 flex-[2] md:min-h-10"
          onChange={(e) => handleFilterTextChange(e.target.value)}
        />
        <Select
          value={bookmarkFilter}
          onValueChange={(value) =>
            setBookmarkFilter(value as 'all' | 'bookmarked' | 'not-bookmarked')
          }
        >
          <SelectTrigger className="min-w-0 flex-1 md:!h-10">
            <SelectValue placeholder="Bookmark status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="bookmarked">Bookmarked</SelectItem>
            <SelectItem value="not-bookmarked">Not Bookmarked</SelectItem>
          </SelectContent>
        </Select>
        {isMobile && (
          <>
            <MobileFilters
              filters={{
                'Document Type': {
                  options: typeFiltersList.map((type) => ({
                    label: type,
                    value: type,
                  })),
                  onFilterChange: (value) => setTypeFilter(value),
                },
                Status: {
                  options: statusFiltersList.map((status) => ({
                    label: status,
                    value: status,
                  })),
                  onFilterChange: (value) => setStatusFilter(value),
                },
                Author: {
                  options: allUsers.map((user) => ({
                    label: user.name ?? 'Unknown',
                    value: user.uid ?? 'Unknown',
                  })),
                  onFilterChange: (value) => setCreatorFilter(value),
                },
              }}
            />
            <MobileSortDialog sorting={sorting} setSorting={setSorting} />
          </>
        )}
        {!isMobile && (
          <>
            <MultiSelect
              options={typeFiltersList.map((type) => ({
                label: type,
                value: type,
              }))}
              onValueChange={(value) => setTypeFilter(value)}
              className="min-w-0 flex-1"
              placeholder="Type"
              maxCount={1}
            />
            <MultiSelect
              options={statusFiltersList.map((status) => ({
                label: status,
                value: status,
              }))}
              onValueChange={(value) => setStatusFilter(value)}
              className="min-w-0 flex-1"
              placeholder="Status"
              maxCount={1}
            />
            <MultiSelect
              options={allUsers.map((user) => ({
                label: user.name ?? 'Unknown',
                value: user.uid ?? 'Unknown',
              }))}
              onValueChange={(value) => setCreatorFilter(value)}
              className="min-w-0 flex-1"
              placeholder="Author"
              maxCount={1}
            />
            <DesktopSortDropdown sorting={sorting} setSorting={setSorting} />
          </>
        )}
      </div>
      <Separator />
      {showDraftBanner && (
        <DraftModeBanner
          isViewingDraft={true}
          customMessage="The content in this tab is only visible to editors and administrators."
          className="rounded-full"
        />
      )}
      {viewType === 'grid' ? (
        <GridView
          documents={gridDocuments}
          className="min-h-0 flex-1"
          projects={projects ?? []}
          currentProjectId={projectId}
          searchQuery={globalFilterInputText}
        />
      ) : (
        <DocumentsTable table={table} className="min-h-[50vh] grow" />
      )}
    </>
  )
}

// Wrapper props shared by all data-fetching wrappers
type DocumentsWrapperProps = {
  projectId: string
  viewType: 'grid' | 'list' | 'table'
  sorting: SortingState
  setSorting: OnChangeFn<SortingState>
  globalFilterInputText: string
  typeFilter: string[]
  setTypeFilter: (value: string[]) => void
  statusFilter: string[]
  setStatusFilter: (value: string[]) => void
  creatorFilter: string[]
  setCreatorFilter: (value: string[]) => void
  bookmarkFilter: 'all' | 'bookmarked' | 'not-bookmarked'
  setBookmarkFilter: (value: 'all' | 'bookmarked' | 'not-bookmarked') => void
  handleFilterTextChange: (value: string) => void
  allUsers: UserInfo[]
  projects: Project[] | null | undefined
  folder?: string
  isMobile: boolean
  showDraftBanner?: boolean
}

// Wrapper for published documents only
function ProjectPublishedDocumentsWrapper({
  projectId,
  viewType,
  sorting,
  setSorting,
  globalFilterInputText,
  typeFilter,
  setTypeFilter,
  statusFilter,
  setStatusFilter,
  creatorFilter,
  setCreatorFilter,
  bookmarkFilter,
  setBookmarkFilter,
  handleFilterTextChange,
  allUsers,
  projects,
  folder,
  isMobile,
  showDraftBanner,
}: DocumentsWrapperProps) {
  const { idToken, TENANT_ID } = useIdTokenAndTenantId()

  // Compute effective folder: root by default, all when searching
  const effectiveFolder = useMemo(() => {
    if (folder) return folder // explicit folder selected
    if (globalFilterInputText) return undefined // search all when query exists
    return 'root' // default: root only
  }, [folder, globalFilterInputText])

  // Fetch bookmarks for filtering and to populate cache for DocumentCardOptions
  const { data: bookmarks } = useSuspenseQuery({
    ...allBookmarksInProjectOptions(idToken!, TENANT_ID!, projectId),
    refetchInterval: REFRESH_INTERVAL,
  })

  // Create a Set of bookmarked document IDs for efficient lookup
  const bookmarkedIds = useMemo(() => {
    const ids = new Set<string>()
    bookmarks?.sops?.forEach((doc) => ids.add(doc.id))
    bookmarks?.documents?.forEach((doc) => ids.add(doc.id))
    return ids
  }, [bookmarks])

  const { data: publishedDocuments } = useSuspenseQuery({
    ...projectDocumentsOptions({
      token: idToken!,
      tenantId: TENANT_ID!,
      projectId,
      stage: 'published',
      folder: effectiveFolder,
    }),
    refetchInterval: REFRESH_INTERVAL,
  })

  return (
    <ProjectDocumentsDisplay
      documents={publishedDocuments}
      bookmarkedIds={bookmarkedIds}
      viewType={viewType}
      sorting={sorting}
      setSorting={setSorting}
      globalFilterInputText={globalFilterInputText}
      typeFilter={typeFilter}
      setTypeFilter={setTypeFilter}
      statusFilter={statusFilter}
      setStatusFilter={setStatusFilter}
      creatorFilter={creatorFilter}
      setCreatorFilter={setCreatorFilter}
      bookmarkFilter={bookmarkFilter}
      setBookmarkFilter={setBookmarkFilter}
      handleFilterTextChange={handleFilterTextChange}
      allUsers={allUsers}
      projects={projects ?? []}
      projectId={projectId}
      isMobile={isMobile}
      showDraftBanner={showDraftBanner}
    />
  )
}

// Wrapper for draft documents (draft + pending_changes)
function ProjectDraftDocumentsWrapper({
  projectId,
  viewType,
  sorting,
  setSorting,
  globalFilterInputText,
  typeFilter,
  setTypeFilter,
  statusFilter,
  setStatusFilter,
  creatorFilter,
  setCreatorFilter,
  bookmarkFilter,
  setBookmarkFilter,
  handleFilterTextChange,
  allUsers,
  projects,
  folder,
  isMobile,
  showDraftBanner,
}: DocumentsWrapperProps) {
  const { idToken, TENANT_ID } = useIdTokenAndTenantId()

  // Compute effective folder: root by default, all when searching
  const effectiveFolder = useMemo(() => {
    if (folder) return folder // explicit folder selected
    if (globalFilterInputText) return undefined // search all when query exists
    return 'root' // default: root only
  }, [folder, globalFilterInputText])

  // Fetch bookmarks for filtering and to populate cache for DocumentCardOptions
  const { data: bookmarks } = useSuspenseQuery({
    ...allBookmarksInProjectOptions(idToken!, TENANT_ID!, projectId),
    refetchInterval: REFRESH_INTERVAL,
  })

  // Create a Set of bookmarked document IDs for efficient lookup
  const bookmarkedIds = useMemo(() => {
    const ids = new Set<string>()
    bookmarks?.sops?.forEach((doc) => ids.add(doc.id))
    bookmarks?.documents?.forEach((doc) => ids.add(doc.id))
    return ids
  }, [bookmarks])

  const { data: draftDocuments } = useSuspenseQuery({
    ...projectDocumentsOptions({
      token: idToken!,
      tenantId: TENANT_ID!,
      projectId,
      stage: 'draft',
      folder: effectiveFolder,
    }),
    refetchInterval: REFRESH_INTERVAL,
  })

  const { data: pendingChangesDocuments } = useSuspenseQuery({
    ...projectDocumentsOptions({
      token: idToken!,
      tenantId: TENANT_ID!,
      projectId,
      stage: 'pending_changes',
      folder: effectiveFolder,
    }),
    refetchInterval: REFRESH_INTERVAL,
  })

  // Combine draft and pending_changes, dedupe by id
  const draftAndPendingChangesDocuments = useMemo(() => {
    const combined = [...draftDocuments, ...pendingChangesDocuments]
    const seen = new Set<string>()
    return combined.filter((doc) => {
      if (seen.has(doc.id)) return false
      seen.add(doc.id)
      return true
    })
  }, [draftDocuments, pendingChangesDocuments])

  return (
    <ProjectDocumentsDisplay
      documents={draftAndPendingChangesDocuments}
      bookmarkedIds={bookmarkedIds}
      viewType={viewType}
      sorting={sorting}
      setSorting={setSorting}
      globalFilterInputText={globalFilterInputText}
      typeFilter={typeFilter}
      setTypeFilter={setTypeFilter}
      statusFilter={statusFilter}
      setStatusFilter={setStatusFilter}
      creatorFilter={creatorFilter}
      setCreatorFilter={setCreatorFilter}
      bookmarkFilter={bookmarkFilter}
      setBookmarkFilter={setBookmarkFilter}
      handleFilterTextChange={handleFilterTextChange}
      allUsers={allUsers}
      projects={projects ?? []}
      projectId={projectId}
      isMobile={isMobile}
      showDraftBanner={showDraftBanner}
    />
  )
}

// Wrapper for all documents
function ProjectAllDocumentsWrapper({
  projectId,
  viewType,
  sorting,
  setSorting,
  globalFilterInputText,
  typeFilter,
  setTypeFilter,
  statusFilter,
  setStatusFilter,
  creatorFilter,
  setCreatorFilter,
  bookmarkFilter,
  setBookmarkFilter,
  handleFilterTextChange,
  allUsers,
  projects,
  folder,
  isMobile,
  showDraftBanner,
}: DocumentsWrapperProps) {
  const { idToken, TENANT_ID } = useIdTokenAndTenantId()

  // Compute effective folder: root by default, all when searching
  const effectiveFolder = useMemo(() => {
    if (folder) return folder // explicit folder selected
    if (globalFilterInputText) return undefined // search all when query exists
    return 'root' // default: root only
  }, [folder, globalFilterInputText])

  // Fetch bookmarks for filtering and to populate cache for DocumentCardOptions
  const { data: bookmarks } = useSuspenseQuery({
    ...allBookmarksInProjectOptions(idToken!, TENANT_ID!, projectId),
    refetchInterval: REFRESH_INTERVAL,
  })

  // Create a Set of bookmarked document IDs for efficient lookup
  const bookmarkedIds = useMemo(() => {
    const ids = new Set<string>()
    bookmarks?.sops?.forEach((doc) => ids.add(doc.id))
    bookmarks?.documents?.forEach((doc) => ids.add(doc.id))
    return ids
  }, [bookmarks])

  const { data: allDocuments } = useSuspenseQuery({
    ...projectDocumentsOptions({
      token: idToken!,
      tenantId: TENANT_ID!,
      projectId,
      stage: undefined,
      folder: effectiveFolder,
    }),
    refetchInterval: REFRESH_INTERVAL,
  })

  return (
    <ProjectDocumentsDisplay
      documents={allDocuments}
      bookmarkedIds={bookmarkedIds}
      viewType={viewType}
      sorting={sorting}
      setSorting={setSorting}
      globalFilterInputText={globalFilterInputText}
      typeFilter={typeFilter}
      setTypeFilter={setTypeFilter}
      statusFilter={statusFilter}
      setStatusFilter={setStatusFilter}
      creatorFilter={creatorFilter}
      setCreatorFilter={setCreatorFilter}
      bookmarkFilter={bookmarkFilter}
      setBookmarkFilter={setBookmarkFilter}
      handleFilterTextChange={handleFilterTextChange}
      allUsers={allUsers}
      projects={projects ?? []}
      projectId={projectId}
      isMobile={isMobile}
      showDraftBanner={showDraftBanner}
    />
  )
}

// Mobile filters component
type Option = {
  label: string
  value: string
}

type Filter = {
  options: Option[]
  onFilterChange: (value: string[]) => void
}

type MobileFiltersProps = {
  filters: Record<string, Filter>
}

function MobileFilters({ filters }: MobileFiltersProps) {
  const [selected, setSelected] = useState<
    Record<string, Record<string, boolean>>
  >({})
  const [open, setOpen] = useState(false)

  const handleApply = useCallback(() => {
    Object.entries(selected).forEach(([key, value]) => {
      filters[key].onFilterChange(
        Object.entries(value)
          .filter(([_, value]) => value)
          .map(([key]) => key),
      )
    })
    setOpen(false)
  }, [selected, filters])

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="icon" aria-label="Filters">
          <ListFilterIcon className="size-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="flex max-h-[90vh] flex-col">
        <DialogHeader>
          <DialogTitle>Filters</DialogTitle>
        </DialogHeader>
        <div className="flex flex-1 flex-col gap-2 overflow-y-scroll">
          <Accordion type="multiple">
            {Object.entries(filters).map(([key, filter]) => (
              <AccordionItem value={key} key={key}>
                <AccordionTrigger>{key}</AccordionTrigger>
                <AccordionContent className="flex flex-col gap-2">
                  {filter.options.map((option) => (
                    <Label htmlFor={option.value} key={option.value}>
                      <div className="flex h-8 flex-row items-center gap-2">
                        <Checkbox
                          id={option.value}
                          checked={selected[key]?.[option.value] ?? false}
                          onCheckedChange={(checked) => {
                            setSelected((prev) => ({
                              ...prev,
                              [key]: {
                                ...prev[key],
                                [option.value]: checked === true,
                              },
                            }))
                          }}
                        />
                        {option.label}
                      </div>
                    </Label>
                  ))}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
        <div className="flex flex-row gap-2">
          <Button
            className="flex-1"
            variant="outline"
            onClick={() => setSelected({})}
          >
            Clear
          </Button>
          <Button className="flex-1" variant="default" onClick={handleApply}>
            Apply
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

// Sort dropdown components
type SortDropdownProps = {
  sorting: SortingState
  setSorting: OnChangeFn<SortingState>
}

function DesktopSortDropdown({ sorting, setSorting }: SortDropdownProps) {
  const currentValue = getSortValue(sorting)

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className="h-10 w-10 shrink-0"
          aria-label="Sort"
        >
          <ArrowDownUpIcon className="size-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-auto p-3">
        <p className="mb-2 text-sm font-medium">Sort by</p>
        <RadioGroup
          value={currentValue}
          onValueChange={(value) => {
            const option = SORT_OPTIONS.find((o) => o.value === value)
            if (option) {
              setSorting([{ id: option.id, desc: option.desc }])
            }
          }}
          className="gap-0"
        >
          {SORT_OPTIONS.map((option) => (
            <Label
              key={option.value}
              htmlFor={`desktop-${option.value}`}
              className="flex cursor-pointer items-center gap-3 py-2"
            >
              <RadioGroupItem
                value={option.value}
                id={`desktop-${option.value}`}
              />
              {option.label}
            </Label>
          ))}
        </RadioGroup>
      </PopoverContent>
    </Popover>
  )
}

function MobileSortDialog({ sorting, setSorting }: SortDropdownProps) {
  const [open, setOpen] = useState(false)
  const [selectedValue, setSelectedValue] = useState(() =>
    getSortValue(sorting),
  )

  const handleApply = useCallback(() => {
    const option = SORT_OPTIONS.find((o) => o.value === selectedValue)
    if (option) {
      setSorting([{ id: option.id, desc: option.desc }])
    }
    setOpen(false)
  }, [selectedValue, setSorting])

  // Sync local state when dialog opens
  useEffect(() => {
    if (open) {
      setSelectedValue(getSortValue(sorting))
    }
  }, [open, sorting])

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="icon" aria-label="Sort">
          <ArrowDownUpIcon className="size-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="flex max-h-[90vh] flex-col">
        <DialogHeader>
          <DialogTitle>Sort by</DialogTitle>
        </DialogHeader>
        <RadioGroup
          value={selectedValue}
          onValueChange={setSelectedValue}
          className="gap-0"
        >
          {SORT_OPTIONS.map((option) => (
            <Label
              key={option.value}
              htmlFor={option.value}
              className="flex items-center gap-3 py-3"
            >
              <RadioGroupItem value={option.value} id={option.value} />
              {option.label}
            </Label>
          ))}
        </RadioGroup>
        <DialogFooter className="flex flex-row gap-2 sm:flex-row">
          <Button
            className="flex-1"
            variant="outline"
            onClick={() => setOpen(false)}
          >
            Cancel
          </Button>
          <Button className="flex-1" variant="default" onClick={handleApply}>
            Apply Sort
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
